<template>
    <hox-modal :max-width-px="700" resizable @close="close">
        <template #header>Download</template>

        <div class="download-modal__select-wrapper">
            Format
            <DownloadPlatformSelect
                v-model="platform"
                :show-html-options="showHtmlOptions"
                :show-psd-options="showPsdOptions"
                :show-ae-options="showAeOptions"
                data-testid="asset-download__format-select"
                class="download-modal__select-platform"
            />
        </div>

        <Tabs v-if="platform" :model-value="tab" @onClick="onTabChange">
            <TabPane label="Download" name="downloadOptions">
                <hox-alert v-if="requiresProfileId" :type="alertType">
                    <template #title>
                        <p v-if="hasProfileId">DC Campaign ProfileId</p>
                        <p v-else>Missing DC Campaign ProfileId</p>
                    </template>

                    <template #content>
                        <p v-if="!hasProfileId">
                            Before downloading your ads, set up your campaign feed in DoubleClick to get your Profile ID
                            and add it here:
                        </p>

                        <Input
                            v-model="dcProfileId"
                            class="download-modal__dc-profile-input"
                            :class="{
                                'download-modal__dc-profile-input--error': hasProfileId && !isProfileIdValid
                            }"
                            placeholder="Double Click Profile Id"
                            data-testid="download-modal__dc-profile-id-input"
                        />

                        <span
                            v-show="hasProfileId && !isProfileIdValid"
                            class="download-modal__dc-profile-input-error-message"
                            data-testid="download-modal__dc-profile-id-input-error"
                        >
                            ProfileId needs to be a number
                        </span>
                    </template>
                </hox-alert>

                <hox-alert v-else-if="isFlashtalkingExcel" :type="alertTypeFlashtalking">
                    <template #title>
                        <p v-if="hasFlashtalkingCampaignName && hasFlashtalkingId">
                            FlashTalking Campaign (Creative Library) Name & Reference ID
                        </p>
                        <p v-else-if="hasFlashtalkingCampaignName">Missing FlashTalking Reference ID</p>
                        <p v-else-if="hasFlashtalkingId">Missing FlashTalking Campaign (Creative Library) Name</p>
                        <p v-else>Missing FlashTalking Campaign (Creative Library) Name & Reference ID</p>
                    </template>

                    <template #content>
                        <Input
                            v-model="flashtalkingCampaignName"
                            data-testid="download-modal__flashtalking-campaign-name"
                            class="download-modal__dc-profile-input"
                            placeholder="FlashTalking Campaign (Creative Library) Name"
                        />

                        <Input
                            v-model="flashtalkingCampaignId"
                            data-testid="download-modal__flashtalking-reference-id"
                            class="download-modal__dc-profile-input"
                            :class="{
                                'download-modal__dc-profile-input--error': hasFlashtalkingId && !isFlashtalkingIdValid
                            }"
                            placeholder="Reference ID"
                        />

                        <span
                            v-show="hasFlashtalkingId && !isFlashtalkingIdValid"
                            class="download-modal__dc-profile-input-error-message"
                            data-testid="download-modal__flashtalking-reference-id-error"
                        >
                            Reference ID needs to be a number
                        </span>

                        <div class="download-modal__options-wrapper">
                            <ul>
                                <li>
                                    <Checkbox
                                        v-model="flashtalkingRemoveVersionNameDimensions"
                                        data-testid="download-modal__flashtalking-strip-dimensions"
                                        label="Strip Dimensions"
                                    >
                                        <h4>Strip Dimensions</h4>
                                    </Checkbox>
                                    <p>Strip Dimensions from Version Name</p>
                                </li>
                            </ul>
                        </div>
                    </template>
                </hox-alert>

                <hox-alert v-else type="info">
                    <template #title>
                        <p>This could take a while</p>
                    </template>

                    <template #content>
                        <p>
                            You will be notified when they are ready to download and you will be able to find download
                            links in your notifications.
                        </p>

                        <p v-if="isGenericLiteDownload">
                            <Divider />
                            Please make sure you read the
                            <span class="download-modal__changes-overview-link" @click="onTabChange('changesOverview')">
                                changes overview
                            </span>
                            tab to understand the limitations of this format before downloading.
                        </p>
                    </template>
                </hox-alert>

                <div class="download-modal__options-wrapper">
                    <div v-if="isZipDownload && !isGenericLiteDownload">
                        <ul>
                            <template v-if="isFlashtalking">
                                <li class="download-modal__short-input">
                                    <Checkbox
                                        v-model="ftClickTagCount"
                                        label="Click Tag Count"
                                        data-testid="download-modal__click-tag-count-checkbox"
                                    >
                                        <h4>Click Tag Count</h4>
                                    </Checkbox>
                                    <p>Flashtalking Click tag count</p>

                                    <div class="download-modal__short-input-value">
                                        <Input
                                            v-model="ftClickTagCountValue"
                                            data-testid="download-modal__click-tag-count-input"
                                            @onChange="onClickTagCountChange"
                                        />
                                    </div>
                                </li>

                                <li class="download-modal__long-input">
                                    <Checkbox
                                        v-model="ftBrowserExclusions"
                                        label="Browser Exclusions"
                                        data-testid="download-modal__browser-exclusions-checkbox"
                                    >
                                        <h4>Browser Exclusions</h4>
                                    </Checkbox>
                                    <p>Comma separated list of browsers</p>
                                    <div class="download-modal__long-input-value">
                                        <Input
                                            v-model="ftBrowserExclusionsValue"
                                            data-testid="download-modal__browser-exclusions-input"
                                            @onChange="onBrowserExclusionChange"
                                        />
                                    </div>
                                </li>
                            </template>

                            <li>
                                <Checkbox
                                    v-model="optimiseTextFiles"
                                    label="Optimise Files"
                                    data-testid="download-modal__optimise-files-checkbox"
                                >
                                    <h4>Optimise Files</h4>
                                </Checkbox>
                                <p>Minify HTML, CSS and JS files used by the template</p>
                            </li>

                            <li>
                                <Checkbox
                                    v-model="optimiseFontFiles"
                                    label="Optimise Fonts"
                                    data-testid="download-modal__optimise-fonts-checkbox"
                                >
                                    <h4>Optimise Fonts</h4>
                                </Checkbox>
                                <p>Remove unused glyphs from font files used in the template</p>
                            </li>

                            <li>
                                <Checkbox
                                    v-model="optimiseImages"
                                    label="Optimise Images"
                                    data-testid="download-modal__optimise-images-checkbox"
                                >
                                    <h4>Optimise Images</h4>
                                </Checkbox>
                                <p>This may result in colour profile changes</p>
                            </li>
                            <li v-if="isGenericHTML">
                                <Checkbox
                                    v-model="backupImage"
                                    label="Include backup images"
                                    data-testid="download-modal__backup-images-checkbox"
                                >
                                    <h4>Include backup images</h4>
                                </Checkbox>
                            </li>
                        </ul>
                    </div>

                    <div v-else-if="isVideoDownload">
                        <ul>
                            <li class="download-modal__option-duration download-modal__short-input">
                                <Checkbox
                                    v-model="manualDuration"
                                    label="Capture Audio"
                                    data-testid="download-video-runtime-checkbox"
                                >
                                    <h4>Video runtime</h4>
                                </Checkbox>
                                <p>You can choose to manually specify the video length</p>
                                <div class="download-modal__option-duration-value download-modal__short-input-value">
                                    <Input
                                        v-model="manualDurationValue"
                                        placeholder="0.00"
                                        data-testid="download-video-runtime-value"
                                        @onChange="onDurationChange"
                                    >
                                        <template #append>
                                            <span>sec</span>
                                        </template>
                                    </Input>
                                </div>
                            </li>
                            <li class="download-modal__option-fps">
                                <Checkbox
                                    v-model="manualFps"
                                    label="FPS settings"
                                    data-testid="download-modal__download-video-fps-checkbox"
                                >
                                    <h4>Frame rate</h4>
                                </Checkbox>
                                <p>You can choose to manually specify the video framerate</p>
                                <div class="download-modal__short-input-value">
                                    <Select
                                        v-model="manualFpsValue"
                                        size="small"
                                        data-testid="download-modal__download-video-fps-select"
                                        @onChange="onFpsChange"
                                    >
                                        <Option
                                            v-for="item in fpsOptions"
                                            :key="item"
                                            :value="item"
                                            :data-testid="`download-modal__download-video-fps-select-${item}`"
                                        >
                                            {{ item }}
                                        </Option>
                                    </Select>
                                </div>
                            </li>
                            <li>
                                <Checkbox
                                    v-model="captureAudio"
                                    label="Capture Audio"
                                    data-testid="download-modal__capture-audio-checkbox"
                                >
                                    <h4>Capture Audio</h4>
                                </Checkbox>
                                <p>Capture audio from the template and include it in the output video</p>
                            </li>
                            <li>
                                <Checkbox
                                    v-model="highQuality"
                                    label="High Quality"
                                    data-testid="download-modal__high-quality-checkbox"
                                >
                                    <h4>Export as uncompressed</h4>
                                </Checkbox>
                                <p>You can then re-encode into another format manually</p>
                            </li>
                            <li>
                                <Checkbox
                                    v-model="droppedFramesOptimisation"
                                    label="Optimise Video Frames"
                                    data-testid="download-modal__optimise-video-frames-checkbox"
                                >
                                    <h4>Optimise Video Frames</h4>
                                </Checkbox>
                                <p>
                                    Useful when the banner contains a video with the same fps as the output. Note that
                                    it will make the video generation slower.
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>
            </TabPane>

            <TabPane label="Changes overview" name="changesOverview">
                <DownloadModalPlatformDetails :platform="platform" />
            </TabPane>
        </Tabs>

        <template #footer>
            <Button class="download-modal__button" data-testid="asset-download__button--cancel" @click="close">
                Cancel
            </Button>
            <Button
                class="download-modal__button"
                :disabled="isDownloadDisabled"
                type="primary"
                data-testid="asset-download__button--download"
                @click="processDownloads"
            >
                Download
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { PLATFORM, DOWNLOAD_FORMAT, ZIP_DOWNLOADS, type DownloadFormatType } from "hoxton-shared/enums/exports";

import type { ConstructedDeliverable, LibraryFilters } from "@/types/types";

import { createAEJob, type RequestRenderInput } from "@/utils/ae";
import getAfterEffectsApiInfoQuery from "@/apollo/queries/v2/GetAfterEffectsApiInfo.gql";
import { MasterTemplateLibraryAction } from "@/store/modules/masterTemplateLibrary";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import DownloadPlatformSelect from "@/components/Campaign/DownloadPlatformSelect.vue";
import DownloadModalPlatformDetails from "@/components/DownloadModalPlatformDetails.vue";
import HoxAlert from "@/components/common/Alert.vue";

import { NotificationTypes } from "@/enums/notifications";
import { downloadJob } from "@/services/deliverables/publish";
import { CampaignAction } from "@/store/modules/campaign";
import { JobsAction } from "@/store/modules/jobs";
import { JobStatusToNotificationStatus, JobNames } from "@/enums/jobs";
import { NotificationsAction } from "@/store/modules/notifications";
import computedValuesToComputedOverwrites from "@/utils/computedValuesToComputedOverwrites";
import { useParams } from "@/composables/useParams";

export default defineComponent({
    components: {
        HoxAlert,
        HoxModal,
        DownloadPlatformSelect,
        DownloadModalPlatformDetails
    },
    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        deliverableIdentifiers: {
            type: Array as PropType<string[]>
        },
        showHtmlOptions: {
            required: true,
            type: Boolean
        },
        showPsdOptions: {
            required: true,
            type: Boolean
        },
        showAeOptions: {
            required: true,
            type: Boolean
        },
        libraryFiltersForQuery: {
            required: false,
            type: Object as PropType<LibraryFilters>
        }
    },

    emits: ["close"],

    setup() {
        const { clientId, campaignId } = useParams();

        return {
            clientId,
            campaignId
        };
    },

    data() {
        return {
            captureAudio: true,
            highQuality: false,
            firstDurationChange: true,
            manualDuration: false,
            manualDurationValue: "",
            ftClickTagCount: true,
            ftClickTagCountValue: 1,
            ftBrowserExclusions: true,
            ftBrowserExclusionsValue: "ie8, ie9, ie10, ie11, opera",
            manualFps: false,
            manualFpsValue: "60",
            droppedFramesOptimisation: false,
            optimiseFontFiles: true,
            optimiseImages: true,
            optimiseTextFiles: true,
            backupImage: false,
            platform: "" as DownloadFormatType | "",
            fpsOptions: [24, 25, 30, 60],
            flashtalkingCampaignName: "",
            flashtalkingCampaignId: "",
            flashtalkingRemoveVersionNameDimensions: false,
            afterEffectsApiInfo: null as null | { url: string },
            tab: "downloadOptions"
        };
    },
    computed: {
        alertType() {
            return this.isProfileIdValid ? "success" : "warning";
        },

        alertTypeFlashtalking() {
            return this.hasFlashtalkingCampaignName && this.isFlashtalkingIdValid ? "success" : "warning";
        },

        canDownloadDeliverable() {
            return this.$auth.userCan(this.$auth.Actions.CanDownloadDeliverable, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        dcProfileId: {
            get() {
                return this.$store.state.campaign.dcProfileId ? this.$store.state.campaign.dcProfileId.toString() : "";
            },
            set(dcProfileId: string) {
                this.$store.dispatch(CampaignAction.SetDcProfileId, dcProfileId);
            }
        },

        hasProfileId() {
            return !!this.dcProfileId;
        },

        isDownloadDisabled() {
            return (
                (this.requiresProfileId && !this.isProfileIdValid) ||
                (this.isFlashtalkingExcel && (!this.hasFlashtalkingCampaignName || !this.isFlashtalkingIdValid)) ||
                this.platform === ""
            );
        },

        isProfileIdValid() {
            return this.hasProfileId && this.dcProfileId.match(/^\d+$/);
        },

        isAEVideoDownload() {
            return this.showAeOptions && this.platform === DOWNLOAD_FORMAT.VIDEO;
        },

        isFlashtalkingIdValid() {
            return this.flashtalkingCampaignId && this.flashtalkingCampaignId.match(/^\d+$/);
        },

        isVideoDownload() {
            return !this.showAeOptions && this.platform === DOWNLOAD_FORMAT.VIDEO;
        },

        isGenericLiteDownload() {
            return !this.showAeOptions && this.platform === DOWNLOAD_FORMAT.GENERIC_LITE;
        },

        isZipDownload() {
            return (
                !this.isFlashtalkingAssets &&
                !this.isFlashtalkingExcel &&
                Object.values(ZIP_DOWNLOADS).includes(
                    this.platform as (typeof ZIP_DOWNLOADS)[keyof typeof ZIP_DOWNLOADS]
                )
            );
        },

        platformOpts() {
            const opts = {};

            if (this.isZipDownload) {
                Object.assign(opts, {
                    optimiseFontFiles: this.optimiseFontFiles,
                    optimiseImages: this.optimiseImages,
                    optimiseTextFiles: this.optimiseTextFiles
                });

                if (this.isFlashtalking) {
                    Object.assign(opts, this.ftPlatformOpts);
                }

                if (this.isGenericHTML) {
                    Object.assign(opts, {
                        backupImage: this.backupImage
                    });
                }
            }

            if (this.isVideoDownload) {
                Object.assign(opts, {
                    captureAudio: this.captureAudio,
                    highQuality: this.highQuality,
                    droppedFramesOptimisation: this.droppedFramesOptimisation
                });

                if (this.manualDuration) {
                    Object.assign(opts, {
                        duration: Number.parseFloat(this.manualDurationValue)
                    });
                }

                if (this.manualFps) {
                    Object.assign(opts, {
                        fps: Number.parseFloat(this.manualFpsValue)
                    });
                }
            }

            if (this.requiresProfileId) {
                Object.assign(opts, {
                    dcProfileId: parseInt(this.dcProfileId, 10)
                });
            }

            if (this.isFlashtalkingAssets || this.isFlashtalkingExcel) {
                Object.assign(opts, {
                    flashtalkingCampaignName: this.flashtalkingCampaignName,
                    flashtalkingCampaignId: +this.flashtalkingCampaignId,
                    flashtalkingRemoveVersionNameDimensions: this.flashtalkingRemoveVersionNameDimensions
                });
            }

            return opts;
        },

        ftPlatformOpts() {
            return {
                ...(this.ftClickTagCount && {
                    clickTagCount: this.ftClickTagCountValue
                }),
                ...(this.ftBrowserExclusions && {
                    browserExclusion: this.ftBrowserExclusionsValue.split(",").map(s => s.trim())
                })
            };
        },

        requiresProfileId() {
            return this.platform === PLATFORM.DOUBLECLICK_SV;
        },

        isFlashtalking() {
            return this.platform === PLATFORM.FLASHTALKING;
        },

        isFlashtalkingExcel() {
            return this.platform === PLATFORM.FLASHTALKING_EXCEL;
        },

        isFlashtalkingAssets() {
            return this.platform === PLATFORM.FLASHTALKING_ASSETS;
        },

        isGenericHTML() {
            return this.platform === DOWNLOAD_FORMAT.GENERIC || this.platform === DOWNLOAD_FORMAT.GENERIC_LITE;
        },

        hasFlashtalkingCampaignName() {
            return !!this.flashtalkingCampaignName;
        },

        hasFlashtalkingId() {
            return !!this.flashtalkingCampaignId;
        }
    },

    watch: {
        canDownloadDeliverable() {
            if (!this.canDownloadDeliverable) {
                this.$snackbar.warning("You do not have access to download deliverables");
                this.close();
            }
        },

        platform(newVal) {
            if (
                newVal === PLATFORM.FLASHTALKING ||
                newVal === DOWNLOAD_FORMAT.GENERIC ||
                newVal === DOWNLOAD_FORMAT.GENERIC_LITE
            ) {
                this.optimiseFontFiles = false;
                this.optimiseImages = false;
                this.optimiseTextFiles = false;
            } else {
                this.optimiseFontFiles = true;
                this.optimiseImages = true;
                this.optimiseTextFiles = true;
            }
        }
    },

    apollo: {
        afterEffectsApiInfo: {
            query: getAfterEffectsApiInfoQuery,
            result({ data }) {
                this.$store.dispatch(MasterTemplateLibraryAction.SetAfterEffectsApiInfo, data.afterEffectsApiInfo);
            }
        }
    },

    methods: {
        close() {
            this.$emit("close");
        },

        onDurationChange() {
            if (this.firstDurationChange && this.manualDurationValue && !this.manualDuration) {
                this.manualDuration = true;
                this.firstDurationChange = false;
            }
        },

        onBrowserExclusionChange() {
            if (!this.ftBrowserExclusions) {
                this.ftBrowserExclusions = true;
            }
        },

        onClickTagCountChange() {
            if (!this.ftClickTagCount) {
                this.ftClickTagCount = true;
            }
        },

        onFpsChange() {
            if (!this.manualDuration) {
                this.manualFps = true;
            }
        },

        onTabChange(tab: "downloadOptions" | "changesOverview") {
            this.tab = tab;
        },

        async processDownloads() {
            if (this.isAEVideoDownload && this.platform === DOWNLOAD_FORMAT.VIDEO) {
                try {
                    this.close();
                    const inputs = this.deliverables.reduce<RequestRenderInput[]>((acc, deliverable) => {
                        if (deliverable.masterTemplate.adType !== TemplateType.AE) {
                            return acc;
                        }

                        const computedOverwrites = computedValuesToComputedOverwrites(deliverable.computedValues);
                        computedOverwrites.forEach((computedOverwrite, index) => {
                            const settings = computedOverwrite?.settings;

                            if (settings) {
                                const layerManagement = {
                                    ...(settings.fontSize ? { fontSize: settings.fontSize } : {}),
                                    ...(settings.font ? { font: settings.font } : {})
                                };

                                if (Object.keys(layerManagement).length) {
                                    computedOverwrites[index].layerManagement = layerManagement;
                                }
                            }
                        });

                        acc.push({
                            reportingLabel: deliverable.reportingLabel,
                            aeInfo: deliverable.masterTemplate?.aeInfo,
                            computedOverwrites
                        });

                        return acc;
                    }, []);

                    const job = await createAEJob({
                        aeApiInfo: {
                            baseUrl: this.afterEffectsApiInfo!.url
                        },
                        inputs,
                        user: {
                            id: this.$store.state.auth.me?.id as string,
                            name: this.$store.state.auth.me?.role.name as string
                        },
                        campaignId: this.campaignId
                    });

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        id: job.jobId,
                        message: "Your ads are being prepared for download",
                        status: JobStatusToNotificationStatus[job.status],
                        type: NotificationTypes.PlainlyBatchRender,
                        job: {
                            _id: job.jobId,
                            message: "Your ads are being prepared for download",
                            status: JobStatusToNotificationStatus[job.status],
                            type: NotificationTypes.PlainlyBatchRender
                        }
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job.jobId,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your ads are being prepared for download. Take a look at your notifications to check on progress"
                    );
                } catch {
                    this.$snackbar.error(
                        "There was an unexpected error while preparing your ads for download. Please try again in a few moments"
                    );
                }
            } else {
                this.close();
                const jobNotificationMap = {
                    [JobNames.PUBFT_DOWNLOAD_EXCEL]: NotificationTypes.FlashtalkingDownloadExcel,
                    [JobNames.PUBFT_DOWNLOAD_ASSETS]: NotificationTypes.FlashtalkingDownloadAssets
                };

                if (this.platform === "") {
                    this.$snackbar.error("Please select a download format");
                    return;
                }

                try {
                    const job = await downloadJob(
                        this.campaignId,
                        this.platform,
                        this.deliverables,
                        this.deliverableIdentifiers,
                        this.libraryFiltersForQuery,
                        this.platformOpts
                    );

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        message: job.message,
                        status: JobStatusToNotificationStatus[job.status],
                        type:
                            jobNotificationMap[job.name as keyof typeof jobNotificationMap] ||
                            NotificationTypes.DownloadJob,
                        job
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job._id,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your ads are being prepared for download. Take a look at your notifications to check on progress"
                    );
                } catch {
                    this.$snackbar.error(
                        "There was an unexpected error while preparing your ads for download. Please try again in a few moments"
                    );
                }
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-modal__button {
    margin: 0 0 0 $spacing-small;
}

.download-modal__select-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 0 $spacing;
    width: 500px;
}

.download-modal__select-platform {
    flex: 1 0;
    width: 100%;
    margin-left: 20px;
}

.download-modal__dc-profile-input {
    margin-bottom: $spacing-small;

    &--error {
        border: 1px solid $error-color;

        .ivu-input:focus,
        .ivu-input:hover {
            border-color: $error-color;
        }
    }

    &-error-message {
        color: $error-color;
    }
}

.download-modal__options-wrapper {
    h4 {
        display: inline-block;
    }

    ul {
        list-style: none;

        li {
            margin-bottom: $spacing-semi-small;
        }
    }

    .ivu-checkbox {
        margin-right: $spacing-small;
    }

    p {
        font-size: $font-size-smaller;
        margin-left: 26px;
    }

    .download-modal__short-input {
        &-value {
            width: 78px;
            float: right;
            margin-top: -35px;
            height: 35px;
        }

        .ivu-input-group-append {
            background-color: transparent;
            border: 0 none;
        }
    }

    .download-modal__long-input {
        &-value {
            width: 220px;
            float: right;
            margin-top: -35px;
            height: 35px;
        }

        .ivu-input-group-append {
            background-color: transparent;
            border: 0 none;
        }
    }
}

.download-modal__changes-overview-link {
    color: $primary-color;
    text-decoration: underline;
    cursor: pointer;
}
</style>
